/** Browser-local presentation; the aggregate persists only the two ISO instants. */
export type ScheduleTimes = { scheduledStartAt: string | null; scheduledEndAt: string | null };
export type ScheduleField = 'start' | 'end' | 'duration';
type TimeResult =
  { patch: Partial<ScheduleTimes>; error?: never } | { error: string; patch?: never };
const millisecondsPerMinute = 60_000;
const pad = (value: number, digits = 2) => String(value).padStart(digits, '0');

export function scheduleTimeKey(times: ScheduleTimes) {
  return `${times.scheduledStartAt === null ? '' : Date.parse(times.scheduledStartAt)}:${times.scheduledEndAt === null ? '' : Date.parse(times.scheduledEndAt)}`;
}

export function formatScheduleLocal(value: string | null) {
  if (value === null) return '';
  const date = new Date(value);
  const base = `${pad(date.getFullYear(), 4)}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  // Match native datetime-local serialization so no-op blur preserves fall-back instants.
  return (
    base +
    (date.getSeconds() || date.getMilliseconds()
      ? `:${pad(date.getSeconds())}${date.getMilliseconds() ? `.${pad(date.getMilliseconds(), 3).replace(/0+$/, '')}` : ''}`
      : '')
  );
}

function elapsed(times: ScheduleTimes) {
  if (!times.scheduledStartAt || !times.scheduledEndAt) return null;
  const value = Date.parse(times.scheduledEndAt) - Date.parse(times.scheduledStartAt);
  return Number.isFinite(value) && value > 0 ? value : null;
}

export function durationMinutes(times: ScheduleTimes) {
  const value = elapsed(times);
  return value === null ? '' : (value / millisecondsPerMinute).toFixed(6).replace(/\.?0+$/, '');
}

export function durationSummary(times: ScheduleTimes) {
  const value = elapsed(times);
  if (value === null) return '';
  const hours = Math.floor(value / 3_600_000);
  const minutes = Math.floor((value % 3_600_000) / millisecondsPerMinute);
  const seconds = (value % millisecondsPerMinute) / 1000;
  return [hours ? `${hours}h` : '', minutes ? `${minutes}m` : '', seconds ? `${seconds}s` : '']
    .filter(Boolean)
    .join(' ');
}

function contractIso(value: number) {
  const date = new Date(value);
  return Number.isFinite(date.getTime()) &&
    date.getUTCFullYear() >= 1 &&
    date.getUTCFullYear() <= 9999
    ? date.toISOString()
    : null;
}

function parseLocal(value: string, existing: string | null) {
  // Do not reinterpret the second occurrence of a fall-back clock time on a no-op edit.
  if (existing !== null && formatScheduleLocal(existing) === value) return existing;
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?$/.exec(
    value,
  );
  if (!match) return null;
  const [, yearText, monthText, dayText, hourText, minuteText, secondText = '0', fraction = ''] =
    match;
  const [year, month, day, hour, minute, second, ms] = [
    Number(yearText),
    Number(monthText),
    Number(dayText),
    Number(hourText),
    Number(minuteText),
    Number(secondText),
    Number(fraction.padEnd(3, '0')),
  ];
  if (
    !year ||
    month! < 1 ||
    month! > 12 ||
    day! < 1 ||
    day! > 31 ||
    hour! > 23 ||
    minute! > 59 ||
    second! > 59
  )
    return null;
  // setFullYear avoids the Date constructor's special treatment of years 00–99.
  const date = new Date(0);
  date.setFullYear(year!, month! - 1, day!);
  date.setHours(hour!, minute!, second!, ms!);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month! - 1 ||
    date.getDate() !== day ||
    date.getHours() !== hour ||
    date.getMinutes() !== minute ||
    date.getSeconds() !== second ||
    date.getMilliseconds() !== ms
  )
    return null;
  return contractIso(date.getTime());
}

export function scheduleTimePatch(
  times: ScheduleTimes,
  field: ScheduleField,
  raw: string,
  badInput = false,
): TimeResult {
  if (badInput) return { error: 'Enter a complete, valid value.' };
  if (field !== 'start' && !times.scheduledStartAt)
    return { error: 'Set the start date and time first.' };
  if (!raw)
    return {
      patch:
        field === 'start'
          ? times.scheduledStartAt === null && times.scheduledEndAt === null
            ? {}
            : { scheduledStartAt: null, scheduledEndAt: null }
          : times.scheduledEndAt === null
            ? {}
            : { scheduledEndAt: null },
    };
  if (field === 'duration') {
    const minutes = Number(raw);
    const ms = Math.round(minutes * millisecondsPerMinute);
    if (!Number.isFinite(minutes) || minutes <= 0 || !Number.isFinite(ms) || ms < 1)
      return {
        error: 'Enter a duration greater than zero.',
      };
    const end = contractIso(Date.parse(times.scheduledStartAt!) + ms);
    if (!end) return { error: 'Enter a duration whose end date is between years 0001 and 9999.' };
    return {
      patch:
        Date.parse(end) === Date.parse(times.scheduledEndAt ?? '') ? {} : { scheduledEndAt: end },
    };
  }
  const existing = field === 'start' ? times.scheduledStartAt : times.scheduledEndAt;
  const value = parseLocal(raw, existing);
  if (!value)
    return {
      error:
        'Enter a complete date and time between years 0001 and 9999 that exists in your local timezone.',
    };
  if (existing && Date.parse(value) === Date.parse(existing)) return { patch: {} };
  if (field === 'end')
    return Date.parse(value) > Date.parse(times.scheduledStartAt!)
      ? { patch: { scheduledEndAt: value } }
      : { error: 'Choose an end date and time after the start.' };
  const duration = elapsed(times);
  const end = duration === null ? null : contractIso(Date.parse(value) + duration);
  if (duration !== null && !end)
    return { error: 'Choose an earlier start so the end stays between years 0001 and 9999.' };
  return { patch: { scheduledStartAt: value, scheduledEndAt: end } };
}
